import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PictureAsPdf } from '@mui/icons-material';
import { getEmployeeAbsences } from '../services/absenceAPIs';
import { format, isWithinInterval, parseISO, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import marabesLogo from '../assets/images/logos/Marabes.png';

const getBase64FromUrl = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => {
      console.warn("Failed to load image:", url);
      resolve(null);
    };
  });
};

const formatDuration = (absence) => {
  const type = absence.ty_pe?.toUpperCase();
  if (type === 'LATENESS') {
    if (absence.hours != null && Number(absence.hours) > 0) {
      const mins = Math.round(Number(absence.hours) * 60);
      return `${mins} min${mins !== 1 ? 's' : ''}`;
    }
    return 'N/A';
  }
  if (absence.hours != null && Number(absence.hours) > 0) {
    const totalH = Number(absence.hours);
    const days = Math.floor(totalH / 8);
    const remH = totalH % 8;
    if (days > 0 && remH > 0) return `${days} day${days > 1 ? 's' : ''} ${remH}h`;
    if (days > 0) return `${days} day${days > 1 ? 's' : ''}`;
    return `${remH} hour${remH === 1 ? '' : 's'}`;
  }
  if (absence.days != null && Number(absence.days) > 0) {
    const d = Number(absence.days);
    return `${d} day${d === 1 ? '' : 's'}`;
  }
  return 'N/A';
};

const formatDateDayMonthYear = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'dd-MM-yyyy');
  } catch (err) {
    return 'Invalid Date';
  }
};

const formatAbsenceDate = (record) => {
  const start = record.startDate || record.date;
  const end = record.endDate;
  if (!start) return 'N/A';
  
  const startFormatted = formatDateDayMonthYear(start);
  if (end) {
    const endFormatted = formatDateDayMonthYear(end);
    if (startFormatted !== endFormatted && endFormatted !== 'Invalid Date' && endFormatted !== 'N/A') {
      return `${startFormatted} to ${endFormatted}`;
    }
  }
  return startFormatted;
};

const SimpleAbsenceReportContent = ({ employee }) => {
  const now = new Date();
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Filters
  const currentYearInt = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYearInt);
  const [quickFilter, setQuickFilter] = useState('ALL');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const years = useMemo(() => {
    const list = [];
    for (let i = currentYearInt; i >= currentYearInt - 4; i--) {
      list.push(i);
    }
    return list;
  }, [currentYearInt]);

  useEffect(() => {
    const fetchData = async () => {
      if (!employee) return;
      setLoading(true);
      try {
        const empId = employee.employeeId || employee.id;
        // Fetch selected year and previous year
        const [dataSelected, dataPrev] = await Promise.all([
          getEmployeeAbsences(empId, selectedYear),
          getEmployeeAbsences(empId, selectedYear - 1)
        ]);
        
        // Merge data
        const merged = [...(dataSelected || []), ...(dataPrev || [])];
        
        // Use a Set or a simple filter to ensure no duplicates if the API overlaps
        const uniqueAbsences = merged.filter((val, idx, self) => 
          idx === self.findIndex((t) => t.id === val.id)
        );
        
        setAbsences(uniqueAbsences);
      } catch (error) {
        console.error('Failed to load absences', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [employee, selectedYear]);

  const applyQuickFilter = (val) => {
    setQuickFilter(val);
    const n = new Date();
    if (val === 'THIS_MONTH') {
      setSelectedYear(n.getFullYear());
      setStartDate(startOfMonth(n));
      setEndDate(endOfMonth(n));
    } else if (val === 'THIS_YEAR') {
      setSelectedYear(n.getFullYear());
      setStartDate(startOfYear(n));
      setEndDate(endOfYear(n));
    } else if (val === 'ALL') {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setQuickFilter('ALL');
    setStartDate(null);
    setEndDate(null);
  };

  const handleCustomDateChange = (type, value) => {
    if (type === 'start') setStartDate(value);
    else setEndDate(value);
    setQuickFilter('CUSTOM');

    if (value) {
      const y = value.getFullYear();
      // If selected date is outside current fetch window (selectedYear or selectedYear - 1)
      if (y > selectedYear) {
        setSelectedYear(y);
      } else if (y < selectedYear - 1) {
        setSelectedYear(y + 1); // setting Y to y+1 will fetch y+1 and y
      }
    }
  };

  const filteredAbsences = useMemo(() => {
    return absences.filter(a => {
      if (!startDate || !endDate) return true;
      try {
        const d = new Date(a.startDate || a.date);
        // Include partial days if time is present
        const dTime = d.getTime();
        const start = startDate.setHours(0, 0, 0, 0);
        const end = endDate.setHours(23, 59, 59, 999);
        return dTime >= start && dTime <= end;
      } catch {
        return false;
      }
    });
  }, [absences, startDate, endDate]);

  const sicknessAbsences = filteredAbsences.filter(a => a.ty_pe?.toUpperCase() === 'SICKNESS');
  const personalAbsences = filteredAbsences.filter(a => ['PERSONAL', 'PERSONAL_COMMITMENT'].includes(a.ty_pe?.toUpperCase()));
  const latenessAbsences = filteredAbsences.filter(a => a.ty_pe?.toUpperCase() === 'LATENESS');

  const handleExportPDF = async () => {
    if (!employee) return;
    setExporting(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const margin = 14;
      const pw = doc.internal.pageSize.width;
      const [marabesBase64, axoBase64] = await Promise.all([
        getBase64FromUrl(marabesLogo),
        getBase64FromUrl('/cLogo.png')
      ]);

      if (marabesBase64) doc.addImage(marabesBase64, 'PNG', margin, 10, 30, 10);
      if (axoBase64) doc.addImage(axoBase64, 'PNG', pw - margin - 30, 10, 30, 10);

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(`Absence Report: ${employee.firstName} ${employee.lastName}`, margin, 32);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const periodLabel = (startDate && endDate) ? `Period: ${formatDateDayMonthYear(startDate)} to ${formatDateDayMonthYear(endDate)}` : `Period: All Time`;
      doc.text(periodLabel, margin, 38);

      let currentY = 46;

      const addTable = (title, data) => {
        if (currentY > 260) {
          doc.addPage();
          currentY = 20;
        }
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(`${title} (${data.length})`, margin, currentY);
        
        autoTable(doc, {
          startY: currentY + 4,
          head: [['Date', 'Duration']],
          body: data.length > 0 ? data.map(item => [formatAbsenceDate(item), formatDuration(item)]) : [['No records', '']],
          theme: 'grid',
          styles: { fontSize: 8 },
          headStyles: { fillColor: [30, 41, 59] },
          margin: { left: margin, right: margin }
        });
        currentY = doc.lastAutoTable.finalY + 12;
      };

      addTable('Sickness Absences', sicknessAbsences);
      addTable('Personal Absences', personalAbsences);
      addTable('Lateness', latenessAbsences);

      doc.save(`Absence_Report_${employee.firstName}_${employee.lastName}.pdf`);
    } catch (e) {
      console.error('PDF export failed', e);
      alert('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
      </Box>
    );
  }

  const renderTable = (title, records, titleColor = '#1E293B', headerBg = '#F8FAFC') => (
    <Box sx={{ flex: 1, minWidth: '250px' }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, color: titleColor, borderBottom: `2px solid ${titleColor}`, pb: 0.5 }}>
        {title} ({records.length})
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', maxHeight: '400px', overflowY: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: headerBg }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: headerBg }}>Duration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.length > 0 ? (
              records.map((record, index) => (
                <TableRow key={index} hover>
                  <TableCell>{formatAbsenceDate(record)}</TableCell>
                  <TableCell>{formatDuration(record)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ color: '#64748B', py: 3, fontStyle: 'italic' }}>
                  No records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Filter Bar */}
        <Box sx={{ p: 2, borderBottom: '1px solid #E2E8F0', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', backgroundColor: '#F8FAFC' }}>
          <FormControl size="small" sx={{ minWidth: 120, bgcolor: 'white' }}>
            <InputLabel>Year</InputLabel>
            <Select value={selectedYear} label="Year" onChange={e => handleYearChange(e.target.value)}>
              {years.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140, bgcolor: 'white' }}>
            <InputLabel>Quick Filter</InputLabel>
            <Select value={quickFilter} label="Quick Filter" onChange={e => applyQuickFilter(e.target.value)}>
              <MenuItem value="ALL">All Time</MenuItem>
              <MenuItem value="THIS_MONTH">This Month</MenuItem>
              <MenuItem value="THIS_YEAR">This Year</MenuItem>
              <MenuItem value="CUSTOM">Custom Range</MenuItem>
            </Select>
          </FormControl>
          <DatePicker 
            label="Start Date" 
            value={startDate} 
            onChange={v => handleCustomDateChange('start', v)} 
            format="dd-MM-yyyy"
            slotProps={{ textField: { size: 'small', sx: { bgcolor: 'white', width: 140 } } }} 
          />
          <DatePicker 
            label="End Date" 
            value={endDate} 
            onChange={v => handleCustomDateChange('end', v)} 
            format="dd-MM-yyyy"
            slotProps={{ textField: { size: 'small', sx: { bgcolor: 'white', width: 140 } } }} 
          />
          <Box flex={1} />
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />} 
            onClick={handleExportPDF}
            disabled={exporting}
            sx={{ fontWeight: 'bold' }}
          >
            Export PDF
          </Button>
        </Box>

        {/* Tables Container (Horizontal) */}
        <Box sx={{ p: 2, flex: 1, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {renderTable('Sickness', sicknessAbsences, '#DC2626', '#FEF2F2')}
          {renderTable('Personal', personalAbsences, '#2563EB', '#EFF6FF')}
          {renderTable('Lateness', latenessAbsences, '#D97706', '#FFFBEB')}
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default SimpleAbsenceReportContent;
